import React, { useCallback, useMemo, useState } from "react";
import { Button, ButtonGroup, CircularProgress, IconButton } from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import isAuthenticated from "../../../../components/Utils/isAuthenticated";
import FacultyRankAutocomplete from "../../../../components/Utils/FacultyRankAutocomplete";
import ListSelect from "../../../../components/Utils/ListSelect";
import { roles, rolesDescription } from "../../../../components/Utils/Descriptions";
import CloseIcon from "@mui/icons-material/Close";
import {
	useAddInstructorToFacultyRank,
	useFacultyRank,
} from "../../../../Hooks/useFacultyRankQuery";

const roleDescriptionById = Object.fromEntries(
	rolesDescription.map((roleDescription) => [roleDescription.id, roleDescription.name]),
);

function sortInstructorAssignments(leftAssignment, rightAssignment) {
	if (rightAssignment.courseCount !== leftAssignment.courseCount) {
		return rightAssignment.courseCount - leftAssignment.courseCount;
	}

	const nameComparison = leftAssignment.name.localeCompare(rightAssignment.name);
	if (nameComparison !== 0) {
		return nameComparison;
	}

	return leftAssignment.classRole.localeCompare(rightAssignment.classRole);
}

function sortRoleAssignments(leftRole, rightRole) {
	const leftRoleIndex = roles.indexOf(leftRole.roleId);
	const rightRoleIndex = roles.indexOf(rightRole.roleId);

	if (leftRoleIndex !== -1 && rightRoleIndex !== -1) {
		return leftRoleIndex - rightRoleIndex;
	}

	return leftRole.roleId.localeCompare(rightRole.roleId);
}

function findScrollableParent(element) {
	let parentElement = element?.parentElement;

	while (parentElement) {
		const overflowY = window.getComputedStyle(parentElement).overflowY;
		const canScroll =
			(overflowY === "auto" || overflowY === "scroll") &&
			parentElement.scrollHeight > parentElement.clientHeight;

		if (canScroll) {
			return parentElement;
		}

		parentElement = parentElement.parentElement;
	}

	return null;
}

function handleNestedScrollableWheel(event) {
	const scrollElement = event.currentTarget;
	const deltaY = event.deltaY;
	const isAtTop = scrollElement.scrollTop <= 0;
	const isAtBottom =
		Math.ceil(scrollElement.scrollTop + scrollElement.clientHeight) >= scrollElement.scrollHeight;
	const shouldHandOffScroll = (deltaY < 0 && isAtTop) || (deltaY > 0 && isAtBottom);

	if (!shouldHandOffScroll) {
		return;
	}

	const scrollableParent = findScrollableParent(scrollElement);
	if (!scrollableParent) {
		return;
	}

	scrollableParent.scrollTop += deltaY;
	event.preventDefault();
}

export default function BulkInstructorManager({
	selectedBulkCourses,
	selectedBulkClassIds,
	onAddInstructorToSelection,
	onRemoveInstructorFromSelection,
	onRemoveRoleFromSelection,
	embedded = false,
}) {
	const auth = isAuthenticated();
	const token = auth.token;
	const deptId = selectedBulkCourses[0]?.dept ?? "";
	const { data: facultyRank = [], isLoading: facultyRankLoading } = useFacultyRank(token);
	const { addInstructorToFacultyRank } = useAddInstructorToFacultyRank(token, deptId);
	const [selectedInstructor, setSelectedInstructor] = useState(null);
	const [selectedRole, setSelectedRole] = useState("");
	const [isApplying, setIsApplying] = useState(false);
	const [assignmentView, setAssignmentView] = useState("roles");
	const [addingNewInstructor, setAddingNewInstructor] = useState(false);

	const missingInstructorCourses = useMemo(
		() => selectedBulkCourses.filter((course) => !course.roles || course.roles.length === 0),
		[selectedBulkCourses],
	);

	const instructorAssignments = useMemo(() => {
		const assignmentsByKey = new Map();

		selectedBulkCourses.forEach((course) => {
			const rolesForCourse = Array.isArray(course.roles) ? course.roles : [];
			const countedKeys = new Set();

			rolesForCourse.forEach((role) => {
				const assignmentKey = `${role.empid}-${role.class_role}`;
				if (countedKeys.has(assignmentKey)) {
					return;
				}

				countedKeys.add(assignmentKey);
				const existingAssignment = assignmentsByKey.get(assignmentKey);

				if (existingAssignment) {
					existingAssignment.courseCount += 1;
					return;
				}

				assignmentsByKey.set(assignmentKey, {
					key: assignmentKey,
					empid: role.empid,
					name: role.name,
					classRole: role.class_role,
					roleDescription:
						role.role_description || roleDescriptionById[role.class_role] || role.class_role,
					courseCount: 1,
				});
			});
		});

		return Array.from(assignmentsByKey.values()).sort(sortInstructorAssignments);
	}, [selectedBulkCourses]);

	const roleAssignments = useMemo(() => {
		const assignmentsByRole = new Map();

		selectedBulkCourses.forEach((course) => {
			const rolesForCourse = Array.isArray(course.roles) ? course.roles : [];
			const countedRolesForCourse = new Set();

			rolesForCourse.forEach((role) => {
				const roleId = String(role.class_role || "").trim().toUpperCase();
				if (!roleId) {
					return;
				}

				const existingAssignment = assignmentsByRole.get(roleId) || {
					roleId,
					roleDescription: role.role_description || roleDescriptionById[roleId] || roleId,
					instructorCount: 0,
					courseCount: 0,
				};

				existingAssignment.instructorCount += 1;
				if (!countedRolesForCourse.has(roleId)) {
					existingAssignment.courseCount += 1;
					countedRolesForCourse.add(roleId);
				}

				assignmentsByRole.set(roleId, existingAssignment);
			});
		});

		return Array.from(assignmentsByRole.values()).sort(sortRoleAssignments);
	}, [selectedBulkCourses]);

	const bulkAutocompleteRow = useMemo(
		() => ({
			classid: "bulk-selection",
			roles: [],
		}),
		[],
	);

	const checkExistingFacultyRank = useCallback(
		(empid) => facultyRank.some((rank) => Number(rank.empid) === Number(empid)),
		[facultyRank],
	);

	const handleCloseAddInstructor = () => {
		setAddingNewInstructor(false);
		setSelectedInstructor(null);
		setSelectedRole("");
	};

	const handleAddInstructor = async () => {
		if (!selectedInstructor || !selectedRole) {
			return;
		}

		setIsApplying(true);
		try {
			onAddInstructorToSelection(selectedInstructor, selectedRole);
			setSelectedInstructor(null);
			setSelectedRole("");
		} finally {
			setIsApplying(false);
		}
	};

	return (
		<div className={embedded ? "" : "rounded-xl border border-gray-200 bg-white p-2 shadow-sm"}>
			

			{missingInstructorCourses.length > 0 && (
				<div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
					<div className="flex items-start gap-2">
						<WarningAmberIcon className="shrink-0" fontSize="small" />
						<div className="min-w-0">
							<p className="text-xs font-semibold uppercase tracking-wide">
								{missingInstructorCourses.length} course
								{missingInstructorCourses.length === 1 ? "" : "s"} missing instructor(s)
							</p>
							<p className="mt-1 text-xs">
								Assign at least one instructor before applying other bulk changes or submitting.
							</p>
							<div className="mt-2 flex flex-wrap gap-2">
								{missingInstructorCourses.map((course) => (
									<span
										key={course.classid}
										className="max-w-full rounded-full border border-amber-200 bg-white px-2 py-1 text-[11px] font-medium"
									>
										{course.classid}: {course.ctitle}
									</span>
								))}
							</div>
						</div>
					</div>
				</div>
			)}
			{addingNewInstructor ? (<div className=" relative mt-1 rounded-lg border border-gray-200 bg-white p-3">
				<IconButton
					aria-label="Cancel adding instructor"
					size="small"
					onClick={handleCloseAddInstructor}
					sx={{
						position: "absolute",
						right: 4,
						top: 4,
						color: "rgb(75 85 99)",
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
				<p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
					Add new instructor to All Selected Courses
				</p>
				<div className="mt-2 flex flex-col gap-2">
					
					<div className="flex flex-row gap-2 items-center">
						<FacultyRankAutocomplete
						options={facultyRank}
						currentRow={bulkAutocompleteRow}
						setExistingRoles={() => {}}
						setNewRoleValue={setSelectedInstructor}
						checkExistingFacultyRank={checkExistingFacultyRank}
						addInstructorToFacultyRank={addInstructorToFacultyRank}
						size="small"
					/>
						<ListSelect
							required
							label="Role"
							marginBottom="0"
							list={roles}
							size="small"
							value={selectedRole}
							onChange={(event) => setSelectedRole(event.target.value)}
							width="125px"
						/>
						<Button
							variant="contained"
							size="small"
							color="success"
							disabled={!selectedInstructor || !selectedRole || isApplying || facultyRankLoading}
							onClick={handleAddInstructor}
							sx={{
								width: "100%",
								minWidth: 0,
								paddingY: 1,
								"@media (min-width:1280px)": {
									width: "auto",
									minWidth: 50,
								},
							}}
						>
							{isApplying ? (
								<CircularProgress size={18} sx={{ color: "white" }} />
							) : (
								<>
									<GroupAddIcon  />
									
								</>
							)}
						</Button>
					</div>
				</div>
			</div>) : (<Button
				variant="outlined"
				color="primary"
				size="small"
				//sx={{ backgroundColor: "white" }}
				fullWidth
				onClick={() => setAddingNewInstructor((adding) => !adding)}
				
				endIcon={<GroupAddIcon />}
			>
				Add New Instructor
			</Button>)}
			

			

			<div className="mt-3 rounded-lg border border-gray-300 bg-white p-3">
				<div className="flex flex-col items-start gap-1 xl:flex-row xl:items-center xl:justify-between xl:gap-3 ">
					<p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
						Currently Assigned {assignmentView === "instructors" ? "Instructors" : "Roles"}
					</p>
					
					<ButtonGroup size="small" variant="outlined" aria-label="Choose assignment removal mode">
						
						<Button
							variant={assignmentView === "roles" ? "contained" : "outlined"}
							onClick={() => setAssignmentView("roles")}
						>
							Roles
						</Button>
						<Button
							variant={assignmentView === "instructors" ? "contained" : "outlined"}
							onClick={() => setAssignmentView("instructors")}
						>
							Instructors
						</Button>
					</ButtonGroup>
				</div>
				{/* <p className="mt-1 text-xs text-gray-500">
					{assignmentView === "instructors"
						? `${instructorAssignments.length} unique instructor/role${
								instructorAssignments.length === 1 ? "" : "s"
							}`
						: `${roleAssignments.length} role${roleAssignments.length === 1 ? "" : "s"}`}
				</p> */}
				<div
					className="mt-2 flex max-h-48 flex-col gap-2 overflow-y-auto px-1  rounded-lg bg-gray-50  py-1"
					onWheel={handleNestedScrollableWheel}
				>
					{assignmentView === "instructors" && instructorAssignments.length > 0 ? (
						instructorAssignments.map((assignment) => (
							<div
								key={assignment.key}
								className="flex flex-col items-stretch gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 xl:flex-row xl:items-center xl:justify-between xl:gap-3"
							>
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-semibold text-gray-900">{assignment.name}</p>
									<p className="break-words text-xs text-gray-600">
										{assignment.classRole} - {assignment.roleDescription}
									</p>
									<p className="text-xs text-gray-500">
										Assigned to {assignment.courseCount} selected course
										{assignment.courseCount === 1 ? "" : "s"}
									</p>
								</div>
								<Button
									variant="outlined"
									color="error"
									onClick={() => onRemoveInstructorFromSelection(assignment)}
									sx={{
										width: "100%",
										minWidth: 0,
										"@media (min-width:1280px)": {
											width: "auto",
											minWidth: 132,
										},
									}}
								>
									<DeleteForeverIcon sx={{ mr: 1 }} />
									Remove
								</Button>
							</div>
						))
					) : assignmentView === "roles" && roleAssignments.length > 0 ? (
						roleAssignments.map((assignment) => (
							<div
								key={assignment.roleId}
								className="flex flex-col items-stretch gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 xl:flex-row xl:items-center xl:justify-between xl:gap-3"
							>
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-semibold text-gray-900">
										{assignment.roleId} - {assignment.roleDescription}
									</p>
									<p className="text-xs text-gray-500">
										{assignment.instructorCount} instructor
										{assignment.instructorCount === 1 ? "" : "s"} assigned across{" "}
										{assignment.courseCount} selected course
										{assignment.courseCount === 1 ? "" : "s"}
									</p>
								</div>
								<Button
									variant="outlined"
									color="error"
									onClick={() => onRemoveRoleFromSelection(assignment)}
									sx={{
										width: "100%",
										minWidth: 0,
										"@media (min-width:1280px)": {
											width: "auto",
											minWidth: 132,
										},
									}}
								>
									<DeleteForeverIcon sx={{ mr: 1 }} />
									Remove Role
								</Button>
							</div>
						))
					) : (
						<div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-4 text-center text-xs text-gray-500">
							No {assignmentView === "instructors" ? "instructors" : "roles"} are assigned across
							the current selection yet.
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
